"use client"

import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react"
import type { FilterSelection, ImportRemainingAs, MappingCounts } from "@/schema/data-import"
import { Label, RadioGroup, RadioGroupItem } from "@ui/shad"
import { Loader2, ShieldUser, UserMinus, Users } from "lucide-react"

import { useApplyMappings, useFetchDataImport, useFetchFilterOptions } from "@/hooks/api/data-imports"
import { MultiSelectInput } from "@/components/_ui/forms"

interface DataMappingsStepProps {
  dataImportId: string
  onNext: () => void
  onButtonLabelChange?: (label: string) => void
}

export interface DataMappingsStepRef {
  submit: () => Promise<void>
  isPending: boolean
  buttonLabel: string
}

export const DataMappingsStep = forwardRef<DataMappingsStepRef, DataMappingsStepProps>(function DataMappingsStep(
  { dataImportId, onNext, onButtonLabelChange },
  ref
) {
  const [clientFilters, setClientFilters] = useState<Record<string, string[]>>({})
  const [pastClientFilters, setPastClientFilters] = useState<Record<string, string[]>>({})
  const [prospectFilters, setProspectFilters] = useState<Record<string, string[]>>({})
  const [importRemainingAs, setImportRemainingAs] = useState<ImportRemainingAs>("other_contact")
  const [result, setResult] = useState<MappingCounts | null>(null)
  const [initialized, setInitialized] = useState(false)

  const { data: dataImport, isLoading: isLoadingImport } = useFetchDataImport(dataImportId)
  const { data: filterOptions, isLoading: isLoadingOptions } = useFetchFilterOptions(dataImportId)
  const applyMappings = useApplyMappings(dataImportId)

  const handleApplyMappings = useCallback(
    async (
      overrideClientFilters?: Record<string, string[]>,
      overridePastClientFilters?: Record<string, string[]>,
      overrideProspectFilters?: Record<string, string[]>,
      overrideImportRemainingAs?: ImportRemainingAs
    ) => {
      const effectiveClientFilters = overrideClientFilters ?? clientFilters
      const effectivePastClientFilters = overridePastClientFilters ?? pastClientFilters
      const effectiveProspectFilters = overrideProspectFilters ?? prospectFilters
      const effectiveImportRemainingAs = overrideImportRemainingAs ?? importRemainingAs

      const toSelections = (filters: Record<string, string[]>): FilterSelection[] =>
        Object.entries(filters)
          .filter(([, values]) => values.length > 0)
          .map(([filter_type, values]) => ({ filter_type, values }))

      const response = await applyMappings.mutateAsync({
        client_filters: toSelections(effectiveClientFilters),
        past_client_filters: toSelections(effectivePastClientFilters),
        prospect_filters: toSelections(effectiveProspectFilters),
        import_remaining_items_as: effectiveImportRemainingAs,
      })

      setResult(response)
    },
    [clientFilters, pastClientFilters, prospectFilters, importRemainingAs, applyMappings]
  )

  useEffect(() => {
    if (initialized || !dataImport) return

    const hasClientMappings = Array.isArray(dataImport.client_mappings) && dataImport.client_mappings.length > 0
    const hasPastClientMappings =
      Array.isArray(dataImport.past_client_mappings) && dataImport.past_client_mappings.length > 0
    const hasProspectMappings = Array.isArray(dataImport.prospect_mappings) && dataImport.prospect_mappings.length > 0

    const newClientFilters: Record<string, string[]> = {}
    const newPastClientFilters: Record<string, string[]> = {}
    const newProspectFilters: Record<string, string[]> = {}

    if (hasClientMappings) {
      dataImport.client_mappings!.forEach((filter: FilterSelection) => {
        newClientFilters[filter.filter_type] = filter.values
      })
      setClientFilters(newClientFilters)
    }

    if (hasPastClientMappings) {
      dataImport.past_client_mappings!.forEach((filter: FilterSelection) => {
        newPastClientFilters[filter.filter_type] = filter.values
      })
      setPastClientFilters(newPastClientFilters)
    }

    if (hasProspectMappings) {
      dataImport.prospect_mappings!.forEach((filter: FilterSelection) => {
        newProspectFilters[filter.filter_type] = filter.values
      })
      setProspectFilters(newProspectFilters)
    }

    // Initialize import_remaining_items_as from backend
    const initialRemainingAs = dataImport.import_remaining_items_as ?? "other_contact"
    setImportRemainingAs(initialRemainingAs)

    setInitialized(true)

    // Calculate counts on initial load
    handleApplyMappings(newClientFilters, newPastClientFilters, newProspectFilters, initialRemainingAs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataImport, initialized])

  // Notify parent when button label changes
  useEffect(() => {
    onButtonLabelChange?.(result ? "Continue to review" : "Apply mappings")
  }, [result, onButtonLabelChange])

  const isLoading = isLoadingImport || isLoadingOptions

  const mappingStepConfig = useMemo(
    () => dataImport?.filter_config.mapping_step ?? [],
    [dataImport?.filter_config.mapping_step]
  )

  const filterTypeLabels = useMemo(
    () => Object.fromEntries(mappingStepConfig.map((f) => [f.type, f.label])),
    [mappingStepConfig]
  )

  const filterGroups = useMemo(() => {
    if (!filterOptions || !dataImport) return {}
    const allowedTypes = mappingStepConfig.map((f) => f.type)
    return filterOptions
      .filter((option) => allowedTypes.includes(option.filter_type))
      .reduce(
        (acc, option) => {
          if (!acc[option.filter_type]) {
            acc[option.filter_type] = []
          }
          acc[option.filter_type].push({
            value: option.filter_id,
            label: option.filter_label,
          })
          return acc
        },
        {} as Record<string, { value: string; label: string }[]>
      )
  }, [filterOptions, dataImport, mappingStepConfig])

  const handleClientFilterChange = (filterType: string, values: string[]) => {
    const newClientFilters = { ...clientFilters, [filterType]: values }
    setClientFilters(newClientFilters)
    handleApplyMappings(newClientFilters, pastClientFilters, prospectFilters, importRemainingAs)
  }

  const handlePastClientFilterChange = (filterType: string, values: string[]) => {
    const newPastClientFilters = { ...pastClientFilters, [filterType]: values }
    setPastClientFilters(newPastClientFilters)
    handleApplyMappings(clientFilters, newPastClientFilters, prospectFilters, importRemainingAs)
  }

  const handleProspectFilterChange = (filterType: string, values: string[]) => {
    const newProspectFilters = { ...prospectFilters, [filterType]: values }
    setProspectFilters(newProspectFilters)
    handleApplyMappings(clientFilters, pastClientFilters, newProspectFilters, importRemainingAs)
  }

  const handleImportRemainingAsChange = (value: ImportRemainingAs) => {
    setImportRemainingAs(value)
    handleApplyMappings(clientFilters, pastClientFilters, prospectFilters, value)
  }

  const handleSubmit = async () => {
    if (result) {
      onNext()
    } else {
      await handleApplyMappings()
    }
  }

  useImperativeHandle(ref, () => ({
    submit: handleSubmit,
    isPending: applyMappings.isPending,
    buttonLabel: result ? "Continue to review" : "Apply mappings",
  }))

  if (isLoading) {
    return (
      <div className="mx-auto flex max-w-xl items-center justify-center py-12">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl">
      <h3 className="mb-2 text-xl font-semibold">Data mappings</h3>

      <div className="flex gap-4">
        <div className="space-y-4">
          <div className="space-y-2 rounded-lg border border-dashed px-4 py-4">
            <div className="flex items-center gap-1.5">
              <ShieldUser className="text-icon size-4.5" />
              <h4 className="font-medium">Who becomes a client?</h4>
            </div>
            <p className="text-muted-foreground text-sm">
              Contacts matching <strong>any</strong> of these filters will be imported as clients.
            </p>

            {mappingStepConfig.map(({ type: filterType }) => {
              const options = filterGroups[filterType]
              if (!options || options.length === 0) return null
              const label = filterTypeLabels[filterType] || filterType
              return (
                <MultiSelectInput
                  key={`client-${filterType}`}
                  id={`client-filter-${filterType}`}
                  label={label}
                  placeholder={`Select ${label.toLowerCase()}`}
                  options={options}
                  values={clientFilters[filterType] || []}
                  onChange={(values) => handleClientFilterChange(filterType, values)}
                />
              )
            })}
          </div>

          <div className="space-y-2 rounded-lg border border-dashed px-4 py-4">
            <div className="flex items-center gap-1.5">
              <UserMinus className="text-icon size-4.5" />
              <h4 className="font-medium">Who becomes a past client?</h4>
            </div>
            <p className="text-muted-foreground text-sm">
              Remaining contacts matching <strong>any</strong> of these filters will be imported as past clients.
            </p>

            {mappingStepConfig.map(({ type: filterType }) => {
              const options = filterGroups[filterType]
              if (!options || options.length === 0) return null
              const label = filterTypeLabels[filterType] || filterType
              return (
                <MultiSelectInput
                  key={`past-client-${filterType}`}
                  id={`past-client-filter-${filterType}`}
                  label={label}
                  placeholder={`Select ${label.toLowerCase()}`}
                  options={options}
                  values={pastClientFilters[filterType] || []}
                  onChange={(values) => handlePastClientFilterChange(filterType, values)}
                />
              )
            })}
          </div>

          <div className="space-y-2 rounded-lg border border-dashed px-4 py-4">
            <div className="flex items-center gap-1.5">
              <Users className="text-icon size-4.5" />
              <h4 className="font-medium">Who becomes a prospect?</h4>
            </div>
            <p className="text-muted-foreground text-sm">
              Remaining contacts matching <strong>any</strong> of these filters will be imported as prospects.
            </p>

            {mappingStepConfig.map(({ type: filterType }) => {
              const options = filterGroups[filterType]
              if (!options || options.length === 0) return null
              const label = filterTypeLabels[filterType] || filterType
              return (
                <MultiSelectInput
                  key={`prospect-${filterType}`}
                  id={`prospect-filter-${filterType}`}
                  label={label}
                  placeholder={`Select ${label.toLowerCase()}`}
                  options={options}
                  values={prospectFilters[filterType] || []}
                  onChange={(values) => handleProspectFilterChange(filterType, values)}
                />
              )
            })}
          </div>

          <div>
            <h4 className="mb-2 font-medium">What should remaining contacts become?</h4>
            <p className="text-muted-foreground mb-4 text-sm">
              Contacts that don&apos;t match client, past client, or prospect filters will be imported as:
            </p>
            <RadioGroup
              value={importRemainingAs}
              onValueChange={(value) => handleImportRemainingAsChange(value as ImportRemainingAs)}
              className="space-y-0"
            >
              <label
                htmlFor="other_contact"
                className="flex cursor-pointer items-center space-x-3 rounded-t-lg border border-b-0 border-dashed px-4 py-2"
              >
                <RadioGroupItem value="other_contact" id="other_contact" />
                <Label htmlFor="other_contact" className="cursor-pointer">
                  Other contacts
                </Label>
              </label>
              <label
                htmlFor="prospect"
                className="flex cursor-pointer items-center space-x-3 rounded-b-lg border border-dashed px-4 py-2"
              >
                <RadioGroupItem value="prospect" id="prospect" />
                <Label htmlFor="prospect" className="cursor-pointer">
                  Prospects
                </Label>
              </label>
            </RadioGroup>
          </div>
        </div>

        <div className="w-64 flex-none space-y-2">
          <div className="space-y-2 rounded-lg border border-dashed px-4 py-4">
            <div className="flex h-6 items-center justify-between gap-1">
              <span className="text-muted-foreground text-sm">Clients</span>
              <span className="h-3 grow border-b border-dashed"></span>
              {applyMappings.isPending ? (
                <Loader2 className="text-icon size-4 animate-spin" />
              ) : (
                <span className="font-medium">{result?.clients_count ?? ""}</span>
              )}
            </div>
            <div className="flex h-6 items-center justify-between gap-1">
              <span className="text-muted-foreground text-sm">Past clients</span>
              <span className="h-3 grow border-b border-dashed"></span>
              {applyMappings.isPending ? (
                <Loader2 className="text-icon size-4 animate-spin" />
              ) : (
                <span className="font-medium">{result?.past_clients_count ?? ""}</span>
              )}
            </div>
            <div className="flex h-6 items-center justify-between gap-1">
              <span className="text-muted-foreground text-sm">Prospects</span>
              <span className="h-3 grow border-b border-dashed"></span>
              {applyMappings.isPending ? (
                <Loader2 className="text-icon size-4 animate-spin" />
              ) : (
                <span className="font-medium">{result?.prospects_count ?? ""}</span>
              )}
            </div>
            <div className="flex h-6 items-center justify-between gap-1">
              <span className="text-muted-foreground text-sm">Other contacts</span>
              <span className="h-3 grow border-b border-dashed"></span>
              {applyMappings.isPending ? (
                <Loader2 className="text-icon size-4 animate-spin" />
              ) : (
                <span className="font-medium">{result?.other_contacts_count ?? ""}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})
